import { IconButton } from "@circleco/compass/components/IconButton";
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { IMAGE_FRAME_CLASS_NAME } from "./constants";

interface DetailsModalLoaderProps {
  isLgScreen: boolean;
  onClose: () => void;
}

export const DetailsModalLoader = ({
  isLgScreen,
  onClose,
}: DetailsModalLoaderProps) => (
  <div
    className={classNames(
      "grid min-h-0 items-stretch overflow-hidden",
      isLgScreen && ["lg:-mx-6 lg:-mb-8", "lg:grid-cols-[65fr_35fr]"],
      !isLgScreen && "gap-6",
    )}
  >
    <div className="min-w-0 lg:min-h-0">
      <div
        className={classNames(
          IMAGE_FRAME_CLASS_NAME,
          "lg:max-h-[calc(100dvh-4rem)]",
        )}
      >
        <Skeleton shape="square" className="absolute inset-0 size-full" />
      </div>
    </div>
    <div className="relative min-w-0 lg:min-h-0">
      <div className="lg:absolute lg:inset-0">
        <div className="border-primary bg-primary flex min-h-0 flex-col overflow-hidden lg:h-full">
          <div className="border-primary flex min-h-16 shrink-0 items-center justify-between gap-3 border-b px-5 py-3">
            <Typography
              variant="heading-lg"
              color="primary"
              component="h2"
              className="min-w-0 flex-1 truncate"
            >
              {t("image_space.grid_view.post_details")}
            </Typography>
            <IconButton
              icon="cross"
              aria-label={t("close")}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
          </div>
          <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-5 pb-0">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Skeleton shape="rounded" className="size-10 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="size-8 shrink-0" />
              </div>
              <div className="space-y-2.5">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="size-8" />
                <Skeleton className="size-8" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
            <div className="space-y-4 pt-5">
              <Skeleton className="h-4 w-40" />
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Skeleton shape="rounded" className="size-8 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Skeleton shape="rounded" className="size-8 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-11/12" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
